import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import MaterialIcon from "./MaterialIcon";

const DEFAULT_BUTTON_CLASS =
  "flex w-full items-center justify-between gap-3 rounded-lg bg-white px-4 py-3 text-left font-body-md text-on-surface ring-1 ring-outline-variant transition-all hover:ring-secondary/40 focus:outline-none focus:ring-2 focus:ring-secondary";

const MENU_BASE_CLASS =
  "overflow-y-auto overscroll-contain rounded-xl border border-outline-variant/30 bg-surface-container-lowest p-1.5 shadow-[0_12px_40px_rgba(15,23,42,0.16)]";

const VIEWPORT_PADDING = 12;
const MENU_GAP = 6;
const ITEM_HEIGHT = 44;
const MENU_PADDING = 12;
const MAX_MENU_HEIGHT = 320;

function getPortalRoot() {
  if (typeof document === "undefined") return null;
  let root = document.getElementById("enterprise-select-portal");
  if (!root) {
    root = document.createElement("div");
    root.id = "enterprise-select-portal";
    root.setAttribute("data-portal", "enterprise-select");
    document.body.appendChild(root);
  }
  return root;
}

function measureMenuHeight(optionCount) {
  return Math.min(MAX_MENU_HEIGHT, optionCount * ITEM_HEIGHT + MENU_PADDING);
}

/**
 * Custom dropdown matching enterprise filter styling (deletion-cases page).
 * Set usePortal when the menu must render above table rows / overflow containers.
 */
const EnterpriseFilterSelect = ({
  value,
  onChange,
  options,
  disabled = false,
  minWidth = "160px",
  ariaLabel,
  id,
  className = "",
  buttonClassName = "",
  menuClassName = "",
  align = "left",
  fullWidth = true,
  stopPropagation = false,
  usePortal = false,
  preferPlacement = "auto",
}) => {
  const [open, setOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const selected =
    options.find((option) => option.value === value) ??
    options[0] ??
    { label: "", value: "" };

  const updateMenuPosition = useCallback(() => {
    const button = buttonRef.current;
    if (!button) return;

    const rect = button.getBoundingClientRect();
    const menuWidth = Math.max(rect.width, parseInt(minWidth, 10) || 160);
    const left =
      align === "right"
        ? Math.max(VIEWPORT_PADDING, rect.right - menuWidth)
        : Math.max(VIEWPORT_PADDING, Math.min(rect.left, window.innerWidth - menuWidth - VIEWPORT_PADDING));

    const estimatedHeight = measureMenuHeight(options.length);
    const spaceBelow = window.innerHeight - rect.bottom - MENU_GAP - VIEWPORT_PADDING;
    const spaceAbove = rect.top - MENU_GAP - VIEWPORT_PADDING;

    let openAbove = false;
    if (preferPlacement === "top") {
      openAbove = true;
    } else if (preferPlacement === "bottom") {
      openAbove = false;
    } else if (spaceBelow < estimatedHeight && spaceAbove > spaceBelow) {
      openAbove = true;
    }

    const availableSpace = openAbove ? spaceAbove : spaceBelow;
    const maxHeight = Math.max(
      ITEM_HEIGHT + MENU_PADDING,
      Math.min(estimatedHeight, availableSpace)
    );

    if (openAbove) {
      setMenuPosition({
        top: null,
        bottom: window.innerHeight - rect.top + MENU_GAP,
        left,
        width: menuWidth,
        maxHeight,
        openAbove: true,
      });
      return;
    }

    let top = rect.bottom + MENU_GAP;
    if (top + maxHeight > window.innerHeight - VIEWPORT_PADDING) {
      top = Math.max(VIEWPORT_PADDING, window.innerHeight - VIEWPORT_PADDING - maxHeight);
    }

    setMenuPosition({
      top,
      bottom: null,
      left,
      width: menuWidth,
      maxHeight,
      openAbove: false,
    });
  }, [align, minWidth, options.length, preferPlacement]);

  useLayoutEffect(() => {
    if (!open) {
      setMenuPosition(null);
      return undefined;
    }
    updateMenuPosition();
    return undefined;
  }, [open, updateMenuPosition]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      const target = event.target;
      if (
        rootRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    const handleReposition = () => updateMenuPosition();

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [open, updateMenuPosition]);

  const menuAlignClass = align === "right" ? "right-0" : "left-0";

  const menuContent =
    !usePortal || menuPosition ? (
      <ul
        ref={menuRef}
        role="listbox"
        aria-label={ariaLabel}
        onClick={stopPropagation ? (event) => event.stopPropagation() : undefined}
        onWheel={(event) => event.stopPropagation()}
        className={[
          MENU_BASE_CLASS,
          usePortal
            ? "fixed z-[99999]"
            : `absolute z-50 mt-2 min-w-full ${menuAlignClass}`,
          menuClassName,
        ]
          .filter(Boolean)
          .join(" ")}
        style={
          usePortal && menuPosition
            ? {
                ...(menuPosition.top != null ? { top: menuPosition.top } : {}),
                ...(menuPosition.bottom != null ? { bottom: menuPosition.bottom } : {}),
                left: menuPosition.left,
                width: menuPosition.width,
                maxHeight: menuPosition.maxHeight,
              }
            : undefined
        }
      >
        {options.map((option) => {
          const isSelected = option.value === value;
          return (
            <li key={option.value || `option-${option.label}`}>
              <button
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                className={[
                  "flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-body-md transition-colors",
                  isSelected
                    ? "bg-surface-container-low font-medium text-secondary"
                    : "text-on-surface hover:bg-surface-container-low",
                ].join(" ")}
              >
                <span className="truncate">{option.label}</span>
                {isSelected ? (
                  <MaterialIcon name="check" className="shrink-0 text-[18px] text-secondary" />
                ) : null}
              </button>
            </li>
          );
        })}
      </ul>
    ) : null;

  const portalRoot = usePortal ? getPortalRoot() : null;

  return (
    <div
      ref={rootRef}
      className={["relative", fullWidth ? "w-full" : "", className].filter(Boolean).join(" ")}
      style={fullWidth ? undefined : { minWidth }}
    >
      <button
        ref={buttonRef}
        id={id}
        type="button"
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={(event) => {
          if (stopPropagation) {
            event.stopPropagation();
          }
          setOpen((prev) => !prev);
        }}
        className={[
          buttonClassName || DEFAULT_BUTTON_CLASS,
          open && !buttonClassName ? "ring-2 ring-secondary" : "",
          disabled ? "cursor-not-allowed opacity-60" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <span className="truncate">{selected.label}</span>
        <MaterialIcon
          name="expand_more"
          className={[
            "shrink-0 text-[20px] text-on-surface-variant transition-transform",
            open ? "rotate-180" : "",
          ].join(" ")}
        />
      </button>

      {open && !disabled && menuContent
        ? usePortal && portalRoot
          ? createPortal(menuContent, portalRoot)
          : menuContent
        : null}
    </div>
  );
};

export default EnterpriseFilterSelect;
